import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="expandable-section"
// Collapses a long content block (via max-height or -webkit-line-clamp) and
// shows a "Read more" / "Show less" toggle. Auto-hides the toggle when the
// content already fits without clamping.
//
// Note: when used inside a modal, the element has 0 dimensions while the
// modal is hidden. We use ResizeObserver to re-evaluate once the element
// actually gets a size (e.g. when the modal opens).
export default class extends Controller {
  static targets = ["content", "toggle", "showMoreLabel", "showLessLabel"]
  static classes = ["expanded"]

  connect() {
    this.#updateToggleVisibility()

    if (typeof ResizeObserver !== "undefined" && this.hasContentTarget) {
      this.resizeObserver = new ResizeObserver(() => this.#updateToggleVisibility())
      this.resizeObserver.observe(this.contentTarget)
    }
  }

  disconnect() {
    this.resizeObserver?.disconnect()
  }

  toggle(event) {
    event.preventDefault()
    const expanded = this.element.classList.toggle(this.expandedClass)
    this.showMoreLabelTarget.classList.toggle("d-none", expanded)
    this.showLessLabelTarget.classList.toggle("d-none", !expanded)
  }

  // With -webkit-line-clamp, scrollHeight reflects the full unclamped content
  // while clientHeight reflects the visible clamped region. If they differ,
  // the toggle should show. While the element has 0 size (hidden parent),
  // leave the toggle in its current state and wait for ResizeObserver to fire.
  #updateToggleVisibility() {
    if (!this.hasContentTarget || !this.hasToggleTarget) return
    if (this.element.classList.contains(this.expandedClass)) return
    if (this.contentTarget.clientHeight === 0) return

    const overflows = this.contentTarget.scrollHeight > this.contentTarget.clientHeight + 2
    this.toggleTarget.classList.toggle("d-none", !overflows)
  }
}
